import supabase from "./Supabase";
import { showToast } from "./Toasts";
import { getFileMimeType, generateSafeFilename, storageProxy } from "./Storage";
import { getUserInfo } from "./UserUtil";
import { fetchUserDownloadsPerDay } from "./Benefits";

export async function getFileInfo(fileId) {
    const { data, error } = await supabase
        .from("files")
        .select("*, script_tags(tags(tag_id, name))")
        .eq("file_id", fileId)
        .single();

    if (error) {
        console.error("Error fetching file info:", error);
        return null;
    }

    if (!data) return null;

    return {
        ...data,
        tags: (data.script_tags || []).map((st: any) => st.tags?.name).filter(Boolean) as string[],
    };
}

export async function getFileMatches(title, writers) {
    const { data, error } = await supabase
        .rpc('get_file_matches', {
            search_title: title,
            search_writers: writers
        });

    if (error) {
        console.error('Error getting matches:', error);
        return [];
    }

    return data;
}

export async function checkDownloadLimit(userId) {
    const dailyDownloadLimit = await fetchUserDownloadsPerDay(userId);

    if (dailyDownloadLimit === null) {
        showToast("error", "Unable to verify download limit. Please try again.");
        return null;
    }

    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setUTCHours(0, 0, 0, 0);

    const endOfDay = new Date(now);
    endOfDay.setUTCHours(24, 0, 0, 0);

    const startOfDayISO = startOfDay.toISOString();
    const endOfDayISO = endOfDay.toISOString();

    const { count: downloadCount, error: countError } = await supabase
        .from("user_downloads")
        .select("id", {
            count: "exact",
            head: true,
        })
        .eq("user_id", userId)
        .gte("download_date", startOfDayISO)
        .lt("download_date", endOfDayISO);

    if (countError) {
        showToast("error","Unable to verify download limit. Please try again.");
    }

    return {
        remaining: dailyDownloadLimit - (downloadCount || 0),
        limit: dailyDownloadLimit,
    };
}

export async function getDownloadsByUser(userId) {
    const { data: downloads, error: downloadsError } = await supabase
        .from("user_downloads")
        .select("*, files!inner(*)")
        .eq("user_id", userId)
        .eq("files.record_deleted", false)
        .order("download_date", { ascending: false })

    if (downloadsError) {
        console.error("Error fetching user downloads:", downloadsError);
    }
    return downloads || [];
}

export async function downloadFile(fileID, userID) {
    const fileInfo = await getFileInfo(fileID);
    const userInfo = await getUserInfo(userID);

    if (!fileInfo) {
        showToast("error", "File not found. Please contact support if this persists.");
        return false;
    }

    if (!userInfo) {
        showToast("error", "User not found. Please contact support if this persists.");
        return false;
    }

    if (userInfo?.isBlacklisted) {
        showToast("error", "Your account is currently restricted and cannot download files. Please contact support.");
        return false;
    }

    const filePath = fileInfo["Title"];
    const fileExtension = filePath.split('.').pop();

    const fileNameParts = [
        fileInfo["Script Title"],
        fileInfo["Writer(s)"],
        fileInfo["Episode #"],
        fileInfo["Draft Date"] ? new Date(fileInfo["Draft Date"]).toLocaleDateString() : null
    ].filter(Boolean).join("-"); // Remove any undefined or empty parts
    
    const niceFileName = generateSafeFilename(`${fileNameParts}${fileExtension ? `.${fileExtension}` : '.pdf'}`);

    try {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.maxTouchPoints > 1 && navigator.userAgent.includes('Mac'));

        if (isIOS) {
            const url = await storageProxy(filePath, "url", 60, "download"); // Short TTL for iOS

            if (!url) {
                showToast("error", "Unable to generate download link. Please contact support if this persists.");
                return false;
            }

            // For iOS: Open signed URL directly in new tab
            // This preserves the user's position in the app
            const newWindow = window.open(url as string, '_blank');

            // Fallback if popup blocked
            if (!newWindow) {
                showToast("info", "Please allow popups to download files.");
                // User can click again after allowing popups
                return false;
            }
        } else {
            // For desktop/Android: Download the file
            const downloadBlob = await storageProxy(filePath, "blob", 300, "download");

            if (!downloadBlob) {
                showToast("error", "Unable to download file. Please contact support if this persists.");
                return false;
            }

            const mimeType = await getFileMimeType(filePath, fileID);

            const blob = new Blob([downloadBlob], {
                type: mimeType || "application/pdf",
            });

            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = niceFileName || filePath;
            document.body.appendChild(link);
            link.click();
            
            // Delay revocation to ensure download starts before cleanup
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
            }, 100);
        }

        return true;

    } catch (unexpectedError) {
        showToast("error", "An unexpected error occurred. Please try again.");
        return false;
    }
}

export async function setEditing(file_id: string, user_id: string): Promise<boolean> {
    const userPermissions = await getUserInfo(user_id);
    if (!userPermissions || !(userPermissions.isApprover || userPermissions.isCurator || userPermissions.isIndexer)) {
        if (!userPermissions) {
            showToast('error', 'User permissions could not be verified.');
        } else {
            showToast('error', "You don't have permission to edit scripts.");
        }
        return false;
    }

    if (userPermissions.isIndexer && !userPermissions.isApprover && !userPermissions.isCurator) {
        const { data: indexingRecord, error } = await supabase
            .from("files")
            .select(`indexing_id, "Writer(s)", modified_by_user`)
            .eq("file_id", file_id)
            .single();

        if (error) {
            showToast('error', 'Unable to verify database record. Please try again.');
            return false;
        }

        if (indexingRecord && indexingRecord["Writer(s)"] !== null) {
            if (!indexingRecord.indexing_id && !indexingRecord.modified_by_user) {
                // This record was not created or previously edited by an indexer
                showToast('error', 'Sorry, only admins can edit this record.');
                return false;
            }

            if (!indexingRecord.modified_by_user || (indexingRecord.modified_by_user && indexingRecord.modified_by_user !== user_id)) {
                const { data: indexingData, error: indexingError } = await supabase
                    .from("indexing")
                    .select("index_user_id")
                    .eq("index_id", indexingRecord.indexing_id)
                    .single();

                if (indexingError || !indexingData || indexingData.index_user_id !== user_id) {
                    // This record was created by a different indexer
                    showToast('error', 'You can only edit records you created or previously edited.');
                    return false;
                }
            }
        }
    }

    const now = new Date();

    const { data, error } = await supabase
        .from('files')
        .update({
            user_editing_id: user_id,
            user_editing_timestamp: now.toISOString(),
        })
        .eq('file_id', file_id)
        .is('user_editing_id', null)
        .select();

    if (!data || data.length === 0) {
        const existingData = await getFileInfo(file_id);
        if (existingData && existingData.user_editing_id === user_id) {
            // Already checked out by the same user
            return true;
        }
        showToast('info', 'Already checked out by another user.');
        return false;
    }

    if (error) throw error;
    return true;
}

export async function clearEditing(file_id: string, user_id: string): Promise<boolean> {
    console.log('Clearing editor:', { file_id, user_id });

    const { error } = await supabase
        .from('files')
        .update({
            user_editing_id: null,
            user_editing_timestamp: null,
        })
        .eq('file_id', file_id)
        .eq('user_editing_id', user_id);

    if (error) throw error;
    return true;
}

// Helper function to prepare form data for database operations
const prepareFormDataForDatabase = (data) => {
    const preparedData = { ...data };

    Object.keys(preparedData).forEach(key => {
        const value = preparedData[key];
        if (typeof value === 'string') {
            const trimmed = value.trim();
            preparedData[key] = trimmed === '' ? null : trimmed;
        }
        if (value === undefined) {
            delete preparedData[key];
        }
    });

    // Handle date formatting for Draft Date
    if (preparedData["Draft Date"]) {
        const parsedDate = new Date(preparedData["Draft Date"] + 'T00:00:00');
        preparedData["Draft Date"] = !isNaN(parsedDate.getTime())
            ? parsedDate.toLocaleDateString("en-US")
            : null;
    }

    return preparedData;
};

export async function updateDatabaseRecord(fileID: string, userID: string, formData: any, clearEditor = true) {
    const preparedData = prepareFormDataForDatabase(formData);

    const { data: databaseUpdate, error: updateError } = await supabase
        .from("files")
        .update({
            ...preparedData,
            user_editing_id: clearEditor ? null : userID,
            user_editing_timestamp: clearEditor ? null : new Date().toISOString(),
            last_updated: new Date().toISOString(),
            modified_by_user: userID,
        })
        .eq("file_id", fileID)
        .eq("user_editing_id", userID)
        .select();

    if (!databaseUpdate || databaseUpdate.length === 0) {
        showToast("info", "Failed to update record. It may have been modified by another user.");
        return false;
    }

    if (updateError) {
        const message = `Error updating database record: ${updateError?.message || 'Unknown error'}`;
        showToast("error", message);
        return false;
    }

    showToast("success", "Database record updated!");
    return true;
}

export async function deleteDatabaseRecord(fileID: string, userID: string, flagReason: string = null, showConfirmation: boolean = true) {
    const userInfo = await getUserInfo(userID);
    if (!userInfo || !userInfo.isApprover) {
        showToast("error", "You don't have permission to delete this record.");
        return;
    }

    const { data: deleteData, error: deleteError } = await supabase
        .from("files")
        .update({
            record_deleted: true,
            flag_reason: flagReason,
            user_editing_id: null,
            user_editing_timestamp: null,
            last_updated: new Date().toISOString(),
            modified_by_user: userID,
        })
        .eq("file_id", fileID)
        .select();

    if (!deleteData || deleteData.length === 0) {
        showToast("info", "Failed to delete record. It may have been modified by another user.");
        return;
    }

    if (deleteError) {
        const message = `Error deleting database record: ${deleteError?.message || 'Unknown error'}`;
        showToast("error", message);
        return;
    }

    if (showConfirmation) {
        return showToast("success", `Deleted ${deleteData[0]["Script Title"]} by ${deleteData[0]["Writer(s)"] || 'Unlisted'}`, 1000);
    }
}

export async function restoreDatabaseRecord(fileID: string, userID: string, clearFlag: boolean = false) {
    const userInfo = await getUserInfo(userID);
    if (!userInfo || !userInfo.isApprover) {
        showToast("error", "You don't have permission to restore this record.");
        return;
    }

    const updateData: any = {
        record_deleted: false,
        last_updated: new Date().toISOString(),
        modified_by_user: userID,
    };
    if (clearFlag) {
        updateData.flag_reason = null;
    }

    const { data: restoreData, error: restoreError } = await supabase
        .from("files")
        .update(updateData)
        .eq("file_id", fileID)
        .select();

    if (!restoreData || restoreData.length === 0) {
        showToast("info", "Failed to restore record. It may have been modified by another user.");
        return;
    }

    if (restoreError) {
        const message = `Error restoring database record: ${restoreError?.message || 'Unknown error'}`;
        showToast("error", message);
        return;
    }

    return showToast("success", "Database record restored.", 1000);
}

export async function getDatabaseRecordsByIndexer(userId) {
    const { data: databaseRecords, error: databaseError } = await supabase
        .from("indexing")
        .select("database_id")
        .eq("index_user_id", userId)
        .order("created_at", { ascending: false });

    if (databaseError) {
        console.error("Error fetching indexing records by user:", databaseError);
        return [];
    }

    const ids = (databaseRecords || [])
        .map(r => r.database_id)
        .filter((id): id is string => typeof id === "string" && id.length > 0);

    if (ids.length === 0) {
        return [];
    }

    const { data: filesRecords, error: filesError } = await supabase
        .from("files")
        .select('file_id, "Script Title", "Writer(s)", "Date Added"')
        .in("file_id", ids)
        .eq("record_deleted", false)
        .order("Date Added", { ascending: false });

    if (filesError) {
        console.error("Error fetching database records:", filesError);
        return [];
    }

    return filesRecords || [];
}

export async function getDatabaseExport() {
    const { data, error } = await supabase
        .from("files")
        .select("*, indexed_by:indexing(index_user_id, userData(email))")
        .order("last_updated", { ascending: false });

    const exportData = data?.map(record => {
        const indexerID = record.indexed_by?.index_user_id || '';
        const indexerEmail = record.indexed_by?.userData?.email || '';

        return {
            ...record,
            index_user_id: indexerID,
            indexed_by: indexerEmail,
        };
    });

    if (error) {
        console.error("Error exporting database records:", error);
        return [];
    }

    return exportData || [];
}

export async function getFlaggedRecords() {
    const { data, error } = await supabase
        .from("files")
        .select("*")
        .neq("flag_reason", null)
        .neq("flag_reason", "")
        .eq("record_deleted", false)
        .order("last_updated", { ascending: false });

    if (error) {
        console.error("Error fetching flagged records:", error);
        return [];
    }

    return data || [];
}

export async function getDeletedRecords() {
    const { data, error } = await supabase
        .from("files")
        .select("*")
        .eq("record_deleted", true)
        .order("last_updated", { ascending: false });

    if (error) {
        console.error("Error fetching deleted records:", error);
        return [];
    }

    return data || [];
}

export async function getDuplicatesByHash(type: "storage_hash" | "google_drive_hash", hash: string) {
    const { data, error } = await supabase
        .from("files")
        .select("*, script_tags(tags(tag_id, name))")
        .eq(type, hash)
        .eq("record_deleted", false);

    if (error) {
        console.error("Error fetching duplicates:", error);
        return [];
    }

    return (data || []).map((d: any) => ({
        ...d,
        tags: (d.script_tags || []).map((st: any) => st.tags?.name).filter(Boolean) as string[],
    }));
}

export async function setScriptTags(fileId: string, tagNames: string[]) {
    const uniqueNames = [...new Set(tagNames.map(n => n.trim()).filter(Boolean))];

    // Look up each tag by name; create root-level if not found
    const tagIds: number[] = [];
    for (const name of uniqueNames) {
        const { data: existing } = await supabase
            .from("tags")
            .select("tag_id")
            .eq("name", name)
            .limit(1)
            .maybeSingle();
        if (existing?.tag_id) {
            tagIds.push(existing.tag_id);
        } else {
            const { data: created, error } = await supabase
                .from("tags")
                .insert({ name })
                .select("tag_id")
                .single();
            if (error) {
                console.error("Error creating tag:", name, error);
                return false;
            }
            if (created?.tag_id) tagIds.push(created.tag_id);
        }
    }

    // Replace all tags for this script
    const { error: deleteError } = await supabase
        .from("script_tags")
        .delete()
        .eq("script_id", fileId);

    if (deleteError) {
        console.error("Error clearing script tags:", deleteError);
        return false;
    }

    if (tagIds.length > 0) {
        const { error: insertError } = await supabase
            .from("script_tags")
            .insert(tagIds.map(tag_id => ({ script_id: fileId, tag_id })));
        if (insertError) {
            console.error("Error inserting script tags:", insertError);
            return false;
        }
    }

    return true;
}

export async function clearFlag(fileID: string, userID: string) {
    const userInfo = await getUserInfo(userID);
    if (!userInfo || !userInfo.isApprover) {
        console.error("User does not have permission to clear flags.");
        return false;
    }

    const { data: clearFlagData, error: clearFlagError } = await supabase
        .from("files")
        .update({
            flag_reason: null,
            last_updated: new Date().toISOString(),
            modified_by_user: userID,
        })
        .eq("file_id", fileID)
        .select();

    if (!clearFlagData || clearFlagData.length === 0) {
        console.error("Error clearing flag: No record updated. It may have been modified by another user.");
        return false;
    }

    if (clearFlagError) {
        console.error(`Error clearing flag on database record: ${clearFlagError?.message || 'Unknown error'}`);
        return false;
    }

    return true;
}

export async function updateScriptPreviews(file_id: string, user_id: string): Promise<boolean> {
    const { error } = await supabase.rpc('record_preview', {
        p_file_id: file_id,
        p_user_id: user_id,
        p_user_agent: navigator.userAgent,
    });

    if (error) {
        console.error("Error updating script preview:", error?.message || 'Unknown error');
        return false;
    }

    return true;
}

export async function getPreviewByUser(file_id: string, user_id: string, preview_date: string = null) {
    // Must match Postgres CURRENT_DATE (UTC) used by record_preview, not the browser's local date,
    // or users west of UTC get a false "not previewed yet" during their evening UTC-date rollover.
    const currentDate = new Date().toISOString().slice(0, 10);

    const { data, error } = await supabase
        .from("user_previews")
        .select("*")
        .eq("user_id", user_id)
        .eq("script_id", file_id)
        .eq("preview_date", preview_date || currentDate)
        .order("preview_date", { ascending: false })
        .limit(1)
        .maybeSingle();

    if (error) {
        console.error("Error fetching script preview:", error);
        return null;
    }

    return data || null;
}

export async function getPreviewCount(user_id: string) {
    const now = new Date();

    // Must match Postgres CURRENT_DATE (UTC) used by record_preview; see getPreviewByUser.
    const previewDate = now.toISOString().slice(0, 10);

    const oneMinuteAgoIso = new Date(now.getTime() - 60 * 1000).toISOString();

    const [todayResult, recentResult] = await Promise.all([
        supabase
            .from("user_previews")
            .select("preview_id", { count: "exact", head: true })
            .eq("user_id", user_id)
            .eq("preview_date", previewDate),

        supabase
            .from("user_previews")
            .select("preview_id", { count: "exact", head: true })
            .eq("user_id", user_id)
            .gte("preview_timestamp", oneMinuteAgoIso),
    ]);

    if (todayResult.error || recentResult.error) {
        console.error("Error fetching preview counts:", todayResult.error || recentResult.error);
        return { todayCount: 0, recentCount: 0 };
    }

    return {
        todayCount: todayResult.count || 0,
        recentCount: recentResult.count || 0,
    };
}

export async function checkMD5HashExists(md5Hash: string) {
    const [storageHash, googleHash, indexingHash] = await Promise.all([
        supabase
            .from("files")
            .select("*")
            .eq("storage_hash", md5Hash),

        supabase
            .from("files")
            .select("*")
            .eq("google_drive_hash", md5Hash),
        
        supabase
            .from("indexing")
            .select("*")
            .eq("file_hash", md5Hash),
    ]);

    if (storageHash.error || googleHash.error || indexingHash.error) {
        console.error("Error checking MD5 hash existence:", storageHash.error || googleHash.error || indexingHash.error);
        return { exists: false, source: null };
    }

    return {
        databaseResults: storageHash.data || googleHash.data || [],
        indexingResults: indexingHash.data || [],
    }
}

export async function getAdminDownloadStats() {
    const { data, error } = await supabase.rpc('get_download_stats');
    if (error) {
        console.error("Error fetching download stats:", error);
        return null;
    }
    return data?.[0] || null;
}

export async function getTopDownloadedScripts(limit = 10, daysBack: number | null = null) {
    const { data, error } = await supabase.rpc('get_top_downloaded_scripts', { p_limit: limit, p_days_back: daysBack });
    if (error) {
        console.error("Error fetching top downloaded scripts:", error);
        return [];
    }
    return data || [];
}

export async function getDownloadsByDay(daysBack = 30) {
    const { data, error } = await supabase.rpc('get_downloads_by_day', { p_days_back: daysBack });
    if (error) {
        console.error("Error fetching downloads by day:", error);
        return [];
    }
    return data || [];
}

export async function getTopDownloadUsers(limit = 10, daysBack: number | null = null) {
    const { data, error } = await supabase.rpc('get_top_download_users', { p_limit: limit, p_days_back: daysBack });
    if (error) {
        console.error("Error fetching top download users:", error);
        return [];
    }
    return data || [];
}

export async function getSuspiciousUsers(daysBack = 7) {
    const { data, error } = await supabase.rpc('get_suspicious_users', { p_days_back: daysBack });
    if (error) {
        console.error("Error fetching suspicious users:", error);
        return [];
    }
    return data || [];
}

export async function getBotPatternUsers() {
    const { data, error } = await supabase.rpc('get_bot_pattern_users');
    if (error) {
        console.error("Error fetching bot pattern users:", error);
        return [];
    }
    return data || [];
}

export async function getNewestUsersActivity(limit = 10) {
    const { data, error } = await supabase.rpc('get_newest_users_activity', { p_limit: limit });
    if (error) {
        console.error("Error fetching newest users activity:", error);
        return [];
    }
    return data || [];
}

export async function getRouletteScripts(count = 10) {
    const { data, error } = await supabase.rpc('get_roulette_scripts', { p_count: count });
    if (error) {
        console.error("Error fetching roulette scripts:", error);
        return [];
    }
    return data || [];
}